/** This is its own components outside of Settings Form due to the complexity */
import { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';

import { WhatsAppContentDefinition } from 'modules/graphql/types';
import { getTrackingDomains } from 'modules/mailDomains/selectors';
import { fetchMailDomainsRequest } from 'modules/mailDomains/actions';
import { BottomTooltip } from 'ui/tooltip';
import { FormField } from 'ui/formField';
import { BasicFilterSelect } from 'ui/selects';
import { spacing } from 'ui/theme';

import {
  CheckboxField,
  FieldExpanded,
  getIsEditMode,
  LinkContainer,
  LinkDescription,
  SettingsFieldMoreDetails,
  SettingsReadOnlyText,
  TemplateAndContentFormDataLocale,
  TwoColumnFieldDisplay,
} from '../../common';
import { DEFAULT_LINK_SHORTENING_TRACKING_DOMAIN, SETTINGS_DISPLAY_CONTENT } from '../constants';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useWhatsAppLinkShortening } from '../context/WhatsAppLinkShorteningContext';
import { getShortenedExampleLink } from '../../EditSMS/constants';
import { formatTrackingDomains } from '../../EditSMS/utils';
import { useEditableWhatsApp } from '../hooks/useEditableWhatsApp';

const StyledFilterSelect = styled(BasicFilterSelect)`
  margin-top: ${spacing.threeQuarter};
`;

const StyledTooltip = styled(BottomTooltip)`
  width: 100%;
`;

export const ShortenAndTrackLinksFormSection = () => {
  const dispatch = useDispatch();
  const { view } = useQueryParamContext();
  const { isEditMode, initialTemplate, liveTemplate, setCurrentMediumField } =
    useEditableFieldsContext();
  const {
    isWhatsAppLinkShorteningEnabled,
    localStorageDomain,
    setIsWhatsAppLinkShorteningEnabled,
    defaultWhatsAppLinkShorteningDomain,
  } = useWhatsAppLinkShortening();
  const { isEditableWhatsAppTemplate } = useEditableWhatsApp();

  const whatsAppData = (liveTemplate?.mediumData || {}) as WhatsAppContentDefinition;
  const initialData = (initialTemplate || {}) as TemplateAndContentFormDataLocale;
  const isNewTemplate = !initialData.name?.length;

  /** Get tracking domains */
  const allTrackingDomains = useSelector(getTrackingDomains);
  const availableTrackingDomains = useMemo(() => {
    /** Filter to only include verified domains */
    const verifiedDomains = [...allTrackingDomains].filter(
      ({ isVerified }: { isVerified: boolean }) => isVerified,
    );
    const includeDefaultLinkShorteningDomain = !verifiedDomains.find(
      ({ domain }) => domain === defaultWhatsAppLinkShorteningDomain,
    );
    return includeDefaultLinkShorteningDomain
      ? [...verifiedDomains, { domain: defaultWhatsAppLinkShorteningDomain }]
      : verifiedDomains;
  }, [allTrackingDomains]);

  const isEditModeVisible = getIsEditMode(view, isEditMode) && isEditableWhatsAppTemplate;

  useEffect(() => {
    /** Check if new template or editing existing one (with content set)
     * If new template and no tracking domain provided, initialize to the one in local storage
     */
    if (initialTemplate?.templateId && isNewTemplate) {
      setCurrentMediumField(
        'trackingDomain',
        localStorageDomain || defaultWhatsAppLinkShorteningDomain,
      );
    }
    /** Fetch mail domains */
    dispatch(fetchMailDomainsRequest());
  }, [initialTemplate?.templateId]);

  const isSelectionDisabled = availableTrackingDomains.length === 1;

  return (
    <FormField
      isHorizontalLayout
      labelText={SETTINGS_DISPLAY_CONTENT.shortenAndTrackLinks.labelText}
      data-test="shorten-track-form-field"
    >
      {isEditModeVisible ? (
        <>
          <CheckboxField
            label={SETTINGS_DISPLAY_CONTENT.shortenAndTrackLinks.checkboxLabel}
            onClick={setIsWhatsAppLinkShorteningEnabled}
            status={isWhatsAppLinkShorteningEnabled}
          />
          {isWhatsAppLinkShorteningEnabled && (
            <FieldExpanded>
              <StyledTooltip
                isEnabled={isSelectionDisabled}
                tooltipContent={SETTINGS_DISPLAY_CONTENT.shortenAndTrackLinks.noDomainsErrorText}
              >
                <StyledFilterSelect
                  data-id="tracking-domain-select"
                  defaultValue={whatsAppData.trackingDomain}
                  id="tracking-domain-select"
                  isDisabled={isSelectionDisabled}
                  options={formatTrackingDomains(availableTrackingDomains)}
                  onSelect={(selected) => {
                    const newDomains = selected as string[];
                    setCurrentMediumField('trackingDomain', newDomains[0]);
                  }}
                  isFullWidth
                />
              </StyledTooltip>
              <LinkDescription>
                {SETTINGS_DISPLAY_CONTENT.shortenAndTrackLinks.helpText}
              </LinkDescription>
              <LinkContainer>
                {getShortenedExampleLink(
                  whatsAppData?.trackingDomain || DEFAULT_LINK_SHORTENING_TRACKING_DOMAIN,
                )}
              </LinkContainer>
            </FieldExpanded>
          )}
        </>
      ) : (
        <TwoColumnFieldDisplay>
          <SettingsReadOnlyText
            data-test="shorten-track-on-off"
            isUnset={!isWhatsAppLinkShorteningEnabled}
          >
            {isWhatsAppLinkShorteningEnabled ? 'On' : 'Off'}
          </SettingsReadOnlyText>
          {isWhatsAppLinkShorteningEnabled && whatsAppData.trackingDomain && (
            <SettingsFieldMoreDetails data-test="shorten-track-detail">
              {whatsAppData.trackingDomain}
            </SettingsFieldMoreDetails>
          )}
        </TwoColumnFieldDisplay>
      )}
    </FormField>
  );
};
